<script setup>
    import { ref, computed, nextTick } from 'vue';
    import axios from 'axios';
    import iconQuiz from '@images/icon-quiz.svg';
    import iconCorrect from '@images/icon-correct.svg';
    import iconIncorrect from '@images/icon-incorrect.svg';
    import iconApplause from '@images/icon-applause.svg';
    import iconWave from '@images/icon-wave.svg';
    import splashBg from '@images/splash-bg.webp';
    import splashBgMobile from '@images/splash-bg-mobile.webp';
    import logoShadow from '@images/logo-shadow.webp';
    import logo from '@images/logo.webp';
    import logoEscitil from '@images/logo-escitil-dark.svg';

    const props = defineProps(['question', 'stageTotal', 'answerUrl', 'token', 'status', 'name', 'introVideo', 'videoBase', 'videoVersions', 'logoutUrl', 'campaignEnded']);

    const CORRECT_ADVANCE_DELAY = 1500;
    const MAX_CLIP_MS = 60000;

    const screen = ref(props.campaignEnded ? 'ended' : ({ completed: 'result', in_progress: 'resume' }[props.status] ?? 'intro'));
    const question = ref(props.question);
    const pendingQuestion = ref(null);
    const selected = ref(null);
    const feedback = ref(null);
    const revealKey = ref(null);

    const videoSrc = num => {
        if (!num) return null;
        const version = props.videoVersions?.[num];
        return `${props.videoBase}/${num}.mp4${version ? `?v=${version}` : ''}`;
    };
    const initialClip = props.status == 'intro' || props.status == 'not_started'
        ? props.introVideo
        : question.value?.video;
    const clip = ref(initialClip);
    const videoUrl = computed(() => videoSrc(clip.value));

    // Powitanie startuje wyciszone (autoplay przed interakcją), pierwsza akcja usera odcisza.
    const videoEl = ref(null);
    const muted = ref(true);
    const onClipEnd = ref(null);
    const clipEnded = () => {
        const cb = onClipEnd.value;
        onClipEnd.value = null;
        cb?.();
    };
    const applyMuted = () => {
        if (videoEl.value) videoEl.value.muted = muted.value;
    };
    const unmute = () => {
        muted.value = false;
        applyMuted();
        videoEl.value?.play();
    };
    const playWithSound = () => {
        if (videoEl.value) videoEl.value.currentTime = 0;
        unmute();
    };

    const mainEl = ref(null);
    const focusHeading = () => {
        const h = mainEl.value?.querySelector('h1, h2');
        if (h) {
            h.setAttribute('tabindex', '-1');
            h.focus();
        }
    };

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const viewTransition = async mutate => {
        if (reduceMotion || !document.startViewTransition) {
            mutate();
            await nextTick();
            focusHeading();
            return;
        }
        await document.startViewTransition(async () => {
            mutate();
            await nextTick();
        }).finished.catch(() => {});
        focusHeading();
    };

    const setClip = num => {
        if (num) clip.value = num;
    };
    const preload = num => {
        const src = videoSrc(num);
        if (!src) return;
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'video';
        link.href = src;
        document.head.appendChild(link);
    };

    const completedStages = computed(() => question.value ? question.value.stage - 1 : 0);
    const resumePercent = computed(() => Math.round((completedStages.value / props.stageTotal) * 100));

    const currentStage = computed(() => screen.value === 'result' ? props.stageTotal : (question.value?.stage ?? 0));
    const stages = computed(() => Array.from({ length: props.stageTotal }, (_, i) => ({
        index: i + 1,
        done: i + 1 < currentStage.value || screen.value === 'result',
        active: i + 1 === currentStage.value && screen.value !== 'result',
    })));
    const answerLetter = key => Object.keys(question.value.answers).indexOf(key) >= 0
        ? String.fromCharCode(65 + Object.keys(question.value.answers).indexOf(key))
        : key.toUpperCase();

    const enter = () => {
        unmute();
        viewTransition(() => {
            screen.value = 'question';
            setClip(question.value.video);
        });
    };

    const choose = async key => {
        if (selected.value) return;
        selected.value = key;
        const { data } = await axios.post(props.answerUrl, {
            _token: props.token,
            questionId: question.value.id,
            answer: key,
        });
        if (data.campaignEnded) {
            viewTransition(() => { screen.value = 'ended'; });
            return;
        }
        pendingQuestion.value = data.question;
        setClip(data.video);
        preload(data.question?.video);
        if (data.correctAnswer == null) {
            advance(data.finished);
            return;
        }
        revealKey.value = data.correctAnswer;
        await nextTick();
        await viewTransition(() => {
            feedback.value = { correct: data.correct, correctAnswer: data.correctAnswer };
        });
        if (data.correct) {
            advanceAfterClip(() => advance(data.finished));
        }
    };

    // Po poprawnej odpowiedzi przejdź dalej dopiero gdy klip przejścia dograł
    // (z bezpiecznikiem na wypadek braku/zablokowanego wideo).
    const advanceAfterClip = next => {
        if (!videoUrl.value) {
            setTimeout(next, CORRECT_ADVANCE_DELAY);
            return;
        }
        let done = false;
        const fallback = setTimeout(run, MAX_CLIP_MS);
        function run() {
            if (done) return;
            done = true;
            clearTimeout(fallback);
            next();
        }
        onClipEnd.value = run;
    };

    const advance = finished => {
        if (finished || !pendingQuestion.value) {
            viewTransition(() => { screen.value = 'result'; });
        } else {
            viewTransition(() => showQuestion(pendingQuestion.value));
        }
    };

    const tryAgain = () => {
        viewTransition(() => showQuestion(pendingQuestion.value));
    };

    const showQuestion = next => {
        question.value = next;
        pendingQuestion.value = null;
        selected.value = null;
        feedback.value = null;
        revealKey.value = null;
        setClip(next.video);
    };
</script>

<template>
    <div v-if="screen === 'ended'" class="auth-split quiz-resume quiz-ended">
        <aside class="auth-brand">
            <picture>
                <source media="(max-width: 767.98px)" :srcset="splashBgMobile" />
                <img class="auth-brand-bg" :src="splashBg" alt="" />
            </picture>
            <div class="auth-brand-logo">
                <img class="auth-brand-glow" :src="logoShadow" alt="" />
                <img class="auth-brand-mark" :src="logo" alt="Escape Room z Escitil — Final Challenge" />
            </div>
            <nav class="footer-links">
                <a href="https://pl.egis.health/polityka-prywatnosci" target="_blank" rel="noopener">Polityka prywatności</a>
                <a href="https://pl.egis.health/kontakt" target="_blank" rel="noopener">Kontakt</a>
                <a href="https://pl.egis.health/nota-prawna" target="_blank" rel="noopener">Nota prawna</a>
            </nav>
            <p class="auth-brand-footer">
                EGIS Polska Sp. z o.o.
            </p>
        </aside>
        <div class="auth-content">
            <div class="auth-content-inner quiz-resume-inner quiz-ended-inner">
                <p class="quiz-resume-brand">Escitil Escape Room</p>
                <img class="quiz-resume-wave" :src="iconWave" alt="" />
                <h1 class="quiz-resume-title">
                    Kampania dobiegła końca
                </h1>
                <p class="quiz-resume-lead">
                    Dziękujemy za zainteresowanie.<br class='d-none d-lg-block'>
                    Udział w quizie nie jest już możliwy.
                </p>
                <a class="quiz-logout-link" :href="logoutUrl">Wyloguj się</a>
            </div>
        </div>
    </div>

    <div v-else-if="screen === 'resume'" class="auth-split quiz-resume">
        <aside class="auth-brand">
            <picture>
                <source media="(max-width: 767.98px)" :srcset="splashBgMobile" />
                <img class="auth-brand-bg" :src="splashBg" alt="" />
            </picture>
            <div class="auth-brand-logo">
                <img class="auth-brand-glow" :src="logoShadow" alt="" />
                <img class="auth-brand-mark" :src="logo" alt="Escape Room z Escitil — Final Challenge" />
            </div>
            <nav class="footer-links">
                <a href="https://pl.egis.health/polityka-prywatnosci" target="_blank" rel="noopener">Polityka prywatności</a>
                <a href="https://pl.egis.health/kontakt" target="_blank" rel="noopener">Kontakt</a>
                <a href="https://pl.egis.health/nota-prawna" target="_blank" rel="noopener">Nota prawna</a>
            </nav>
            <p class="auth-brand-footer">
                EGIS Polska Sp. z o.o.
            </p>
        </aside>
        <div class="auth-content">
            <div class="auth-content-inner quiz-resume-inner">
                <p class="quiz-resume-brand">Escitil Escape Room</p>
                <img class="quiz-resume-wave" :src="iconWave" alt="" />
                <h1 class="quiz-resume-title">
                    Witaj z powrotem<span v-if="name">,<br>
                    dr {{ name }}</span>!
                </h1>
                <p class="quiz-resume-lead">
                    Twój quiz jest w trakcie.<br class='d-none d-lg-block'>
                    Możesz go dokończyć w dowolnym momencie.
                </p>
                <div class="quiz-resume-progress">
                    <p class="quiz-resume-progress-label">Twój postęp:</p>
                    <p class="quiz-resume-stage">Etap {{ completedStages }} z {{ stageTotal }}</p>
                    <div class="quiz-resume-bar">
                        <span class="quiz-resume-bar-fill" :style="{ width: resumePercent + '%' }"></span>
                    </div>
                    <p class="quiz-resume-percent">{{ resumePercent }}% ukończono</p>
                </div>
                <button type="button" class="btn btn-primary quiz-cta" @click="enter">
                    Kontynuuj Quiz
                </button>
                <p class="quiz-resume-note">Sesja ważna przez 30 dni od daty rejestracji</p>
            </div>
        </div>
    </div>

    <div
        v-else
        class="quiz"
        :class="{ 'quiz-intro-bg': screen === 'intro', 'quiz-answering': screen === 'question' && !feedback }"
    >
        <aside class="quiz-avatar">
            <div class="quiz-video">
                <img
                    v-if="screen === 'result'"
                    class="quiz-result-logo"
                    :src="logoEscitil"
                    alt="Escitil"
                />
                <video
                    v-if="videoUrl"
                    :key="videoUrl"
                    ref="videoEl"
                    class="quiz-video-el"
                    :src="videoUrl"
                    autoplay
                    :muted="muted"
                    playsinline
                    @loadeddata="applyMuted"
                    @ended="clipEnded"
                ></video>
                <button
                    v-if="muted && videoUrl"
                    type="button"
                    class="quiz-video-play"
                    aria-label="Odtwórz z dźwiękiem"
                    @click="playWithSound"
                >
                    <span class="quiz-video-play-icon"></span>
                </button>
            </div>
        </aside>

        <main class="quiz-main" ref="mainEl">
            <header class="quiz-header">
                <p class="quiz-title">Escitil Escape Room</p>
                <ol v-if="screen === 'question'" class="quiz-nav">
                    <li
                        v-for="stage in stages"
                        :key="stage.index"
                        class="quiz-nav-step"
                        :class="{ 'is-done': stage.done, 'is-active': stage.active }"
                        :aria-current="stage.active ? 'step' : null"
                    >
                        <span class="quiz-nav-label">
                            Etap <strong>{{ stage.index }}</strong> / {{ stageTotal }}
                            <span v-if="stage.done" class="visually-hidden">(ukończono)</span>
                        </span>
                        <span class="quiz-nav-bar"></span>
                    </li>
                </ol>
            </header>

            <section v-if="screen === 'intro'" class="quiz-body quiz-intro">
                <p class="quiz-greeting">
                    Dzień dobry. Dziękuję za przyjęcie mojego zaproszenia i chęć udziału w quizie.
                    Zapraszam do wspólnej zabawy!
                </p>
                <img class="quiz-intro-icon" :src="iconQuiz" alt="" />
                <p class="quiz-intro-count">Quiz składa się z {{ stageTotal }} pytań.</p>
                <p class="quiz-intro-rule">Aby wygrać, odpowiedz poprawnie na każde.</p>
                <button type="button" class="btn btn-primary quiz-cta" @click="enter">
                    Zobacz 1. pytanie
                </button>
            </section>

            <section v-else-if="screen === 'question'" class="quiz-body quiz-question">
                <template v-if="!feedback">
                    <div class="quiz-prompt">
                        <span class="quiz-badge">Pytanie {{ question.stage }}</span>
                        <p class="quiz-hint">Proszę zaznaczyć prawidłową odpowiedź</p>
                        <h2 id="quiz-question-text" class="quiz-question-text">{{ question.text }}</h2>
                    </div>
                    <ul class="quiz-answers" role="group" aria-labelledby="quiz-question-text">
                        <li v-for="(text, key) in question.answers" :key="key">
                            <button
                                type="button"
                                class="quiz-answer"
                                :class="{ 'is-selected': selected === key, 'is-correct': revealKey === key }"
                                :disabled="!!selected"
                                :aria-pressed="selected === key"
                                @click="choose(key)"
                            >
                                <span class="quiz-answer-letter">{{ answerLetter(key) }}</span>
                                <span class="quiz-answer-text">{{ text }}</span>
                            </button>
                        </li>
                    </ul>
                </template>

                <div v-else-if="feedback.correct" class="quiz-feedback quiz-feedback-correct" role="status">
                    <h2 class="quiz-feedback-title">Doskonale!</h2>
                    <img class="quiz-feedback-icon" :src="iconCorrect" alt="" />
                    <p class="quiz-feedback-text">To poprawna odpowiedź.</p>
                    <div class="quiz-answer quiz-answer-hint">
                        <span class="quiz-answer-letter">{{ answerLetter(feedback.correctAnswer) }}</span>
                        <span class="quiz-answer-text">{{ question.answers[feedback.correctAnswer] }}</span>
                    </div>
                </div>

                <div v-else class="quiz-feedback quiz-feedback-wrong" role="status">
                    <h2 class="quiz-feedback-title">Niestety...</h2>
                    <img class="quiz-feedback-icon" :src="iconIncorrect" alt="" />
                    <p class="quiz-feedback-text">
                        To nie jest poprawna odpowiedź.<br />
                        Sprawdź poniżej i spróbuj jeszcze raz.
                    </p>
                    <p class="quiz-answer-label">Poprawna odpowiedź:</p>
                    <div class="quiz-answer quiz-answer-hint">
                        <span class="quiz-answer-letter">{{ answerLetter(feedback.correctAnswer) }}</span>
                        <span class="quiz-answer-text">{{ question.answers[feedback.correctAnswer] }}</span>
                    </div>
                    <button type="button" class="btn btn-primary quiz-cta" @click="tryAgain">
                        Spróbuj jeszcze raz
                    </button>
                </div>
            </section>

            <section v-else class="quiz-body quiz-result">
                <h2 class="quiz-result-title">Gratulacje!</h2>
                <img class="quiz-result-icon" :src="iconApplause" alt="" />
                <p class="quiz-result-text">
                    Gra została ukończona z sukcesem. Kiedy spotkamy się, przekażę nagrodę.
                    Dziękuję za wspólny czas... do zobaczenia wkrótce!
                </p>
                <div class="quiz-score">
                    <p class="quiz-score-label">Twój wynik:</p>
                    <p class="quiz-score-value">{{ stageTotal }} / {{ stageTotal }} - 100%</p>
                    <p class="quiz-score-note">Wszystkie poziomy zaliczone</p>
                </div>
                <a class="quiz-logout-link" :href="logoutUrl">Wyloguj się</a>
                <div class="quiz-result-legal">
                    <p class="quiz-result-sig">
                        ESC/PSYCH/KOME/MP/2026/06/00077, ESC/PSYCH/KOME/MP/2026/04/00125
                    </p>
                    <a
                        class="quiz-result-siol"
                        href="https://l.ead.me/be288s"
                        target="_blank"
                        rel="noopener"
                    >
                        Informacja o leku
                    </a>
                    <p class="quiz-result-drug">
                        Escitil, 10 mg, tabletki powlekane, białe, owalne (8,1 x 5,6 mm) z linią
                        podziałową po jednej stronie, można podzielić na równe dawki. Każda zawiera
                        10mg escytalopramu (Escitalopramum) (w postaci szczawianu). Escitil, 20 mg,
                        tabletki powlekane, białe, owalne (11,6 x 7,1 mm) z linią podziałową po jednej
                        stronie, można podzielić na równe dawki. Każda zawiera 20mg escytalopramu
                        (Escitalopramum) (w postaci szczawianu). Podmiot odpowiedzialny - Egis
                        Pharmaceuticals PLC, wydawany z przepisu lekarza – Rp.
                    </p>
                </div>
            </section>

        </main>

        <footer class="quiz-footer">
            <nav class="footer-links">
                <a href="https://pl.egis.health/polityka-prywatnosci" target="_blank" rel="noopener">Polityka prywatności</a>
                <a href="https://pl.egis.health/kontakt" target="_blank" rel="noopener">Kontakt</a>
                <a href="https://pl.egis.health/nota-prawna" target="_blank" rel="noopener">Nota prawna</a>
            </nav>
            EGIS Polska Sp. z o.o.
        </footer>
    </div>
</template>
